import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLiveModeSync } from "../../hooks/useLiveModeSync";
import {
  INTERVAL_PHASES,
  formatIntervalClock,
  intervalRemainingSec,
  intervalStateFromSync,
  tickInterval,
} from "../../utils/intervalTimer";
import { gamesWon, matchWinnerSide } from "../../utils/matchScoreboard";
import "./LiveTrainingModeTools.css";

function useLocalInterval(synced) {
  const [now, setNow] = useState(() => Date.now());
  const base = useMemo(
    () => (synced ? intervalStateFromSync(synced.state, synced.receivedAt, synced.ageMs) : null),
    [synced]
  );
  useEffect(() => {
    if (!base?.running) return undefined;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [base]);
  if (!base) return null;
  const state = tickInterval(base, now);
  return { state, remainingSec: intervalRemainingSec(state, now) };
}

function isViewer(player, viewerPlayerId, viewerUserId) {
  if (!player) return false;
  if (viewerPlayerId && player.playerId && String(player.playerId) === String(viewerPlayerId)) return true;
  return Boolean(viewerUserId && player.userId && String(player.userId) === String(viewerUserId));
}

/**
 * Player view card for coach-run live modes: interval countdown and the
 * viewer's table score. Renders nothing until the coach publishes state.
 */
export function LiveTrainingModeLiveCard({
  socket,
  tsId,
  teId,
  viewerPlayerId = null,
  viewerUserId = null,
  viewerTableNumber = null,
}) {
  const { t } = useTranslation();
  const { interval, match } = useLiveModeSync({ socket, tsId, teId });
  const local = useLocalInterval(interval);

  const myTable = useMemo(() => {
    const tables = match?.state?.tables || [];
    return (
      tables.find(
        (tbl) =>
          isViewer(tbl.playerA, viewerPlayerId, viewerUserId) ||
          isViewer(tbl.playerB, viewerPlayerId, viewerUserId)
      ) ||
      (viewerTableNumber != null
        ? tables.find((tbl) => String(tbl.tableNumber) === String(viewerTableNumber))
        : null) ||
      null
    );
  }, [match, viewerPlayerId, viewerUserId, viewerTableNumber]);

  const showInterval = local && local.state.phase !== INTERVAL_PHASES.IDLE;
  if (!showInterval && !myTable) return null;

  const bestOf = match?.state?.bestOf || 3;

  return (
    <div className="lt-mode-live">
      {showInterval ? (
        <div className={`lt-mode-live__interval lt-mode-live__interval--${local.state.phase}`} aria-live="polite">
          <span className="lt-mode-live__phase">
            {local.state.phase === INTERVAL_PHASES.WORK
              ? t("liveModeIntervalWork", "Work")
              : local.state.phase === INTERVAL_PHASES.REST
                ? t("liveModeIntervalRest", "Rest")
                : t("liveModeIntervalDone", "Done")}
            {!local.state.running && local.state.phase !== INTERVAL_PHASES.DONE
              ? ` · ${t("liveModeIntervalPaused", "Paused")}`
              : null}
          </span>
          {local.state.phase !== INTERVAL_PHASES.DONE ? (
            <span className="lt-mode-live__clock">{formatIntervalClock(local.remainingSec)}</span>
          ) : null}
          <span className="lt-mode-live__round">
            {t("liveModeIntervalRound", "Round")} {local.state.round}/{local.state.rounds}
          </span>
        </div>
      ) : null}

      {myTable ? (
        <div className="lt-mode-live__match">
          <span className="lt-mode-live__table">
            {t("liveTrainingChronoTable", "Table")} {myTable.tableNumber}
          </span>
          {[myTable.playerA, myTable.playerB].map((player, side) => {
            const won = gamesWon(myTable)[side];
            const winner = matchWinnerSide(myTable, bestOf) === side;
            return (
              <div
                key={side}
                className={`lt-mode-live__side${isViewer(player, viewerPlayerId, viewerUserId) ? " lt-mode-live__side--me" : ""}${
                  winner ? " lt-mode-live__side--winner" : ""
                }`}
              >
                <span className="lt-mode-live__name">{player?.name || "—"}</span>
                <span className="lt-mode-live__games">{won}</span>
                <span className="lt-mode-live__points">{myTable.current[side]}</span>
              </div>
            );
          })}
          {myTable.sets.length ? (
            <span className="lt-mode-live__sets">
              {myTable.sets.map(([a, b]) => `${a}-${b}`).join("  ")}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default LiveTrainingModeLiveCard;
